<script lang="ts" setup>
import { useField, useForm } from 'vee-validate';
import {
  computed,
  nextTick,
  ref,
  toRefs,
  watch,
} from 'vue';
import * as yup from 'yup';

import type { GrammarTextFieldExpose } from '@nora-monorepo/grammar-ui';
import {
  GrammarFooter,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarSecondaryButton,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';

interface TextPromptModalProps {
  modelValue: boolean;
  initialText?: string;
  title?: string;
  textFieldLabel?: string;
  placeholder?: string;
  okLabel?: string;
  cancelLabel?: string;
  showCloseButton?: boolean;
  maxCharacters?: number;
  errorMessage?: string;
}

const props = withDefaults(defineProps<TextPromptModalProps>(), {
  initialText: '',
  title: 'Text prompt',
  textFieldLabel: 'Text',
  placeholder: 'Enter text',
  okLabel: 'OK',
  cancelLabel: 'Cancel',
  showCloseButton: true,
  errorMessage: '',
});

const emits = defineEmits(['update:modelValue', 'save', 'cancel']);

const { modelValue } = toRefs(props);

const textFieldRef = ref<GrammarTextFieldExpose | null>(null);

const schema = yup.object({
  text: props.maxCharacters ? yup.string().max(props.maxCharacters, `Max characters is ${props.maxCharacters}`) : yup.string(),
});

const { handleSubmit, errors } = useForm({
  validationSchema: schema,
  initialValues: {
    text: props.initialText,
  },
});

const { value: text } = useField<string>('text');
const submittedText = ref('');

// errors derived from the parent component, only valid error while the input is unchanged from last submit attempt
const externalErrorForCurrentInput = computed(() => {
  if (!props.errorMessage) {
    return '';
  }

  if (text.value !== submittedText.value) {
    return '';
  }

  return props.errorMessage;
});

const canSave = computed(() => {
  return !errors.value.text && !externalErrorForCurrentInput.value;
});

function onClose() {
  submittedText.value = '';
  text.value = props.initialText;
  emits('cancel');
}

const onSave = handleSubmit((values) => {
  submittedText.value = values.text;
  emits('save', values.text);
});

const inputFieldType = computed(() => {
  if (externalErrorForCurrentInput.value) {
    return 'error';
  }

  if (!props.maxCharacters) {
    return 'default';
  }

  return errors.value.text ? 'error' : 'default';
});

const displayedErrorMessage = computed(() => {
  if (externalErrorForCurrentInput.value) {
    return externalErrorForCurrentInput.value;
  }

  if (!props.maxCharacters) {
    return '';
  }

  return errors.value.text || '';
});

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter' && canSave.value) {
    event.preventDefault();
    onSave();
  }
  else if (event.key === 'Escape') {
    event.preventDefault();
    onClose();
  }
}

async function onModalShow() {
  await nextTick();
  requestAnimationFrame(() => {
    textFieldRef.value?.focus();
  });
}

watch(
  () => props.initialText,
  (newVal) => {
    text.value = newVal;
  },
);
</script>

<template>
  <GrammarModal
    :is-open="modelValue"
    :show-close-button="props.showCloseButton"
    :label="props.title"
    @show="onModalShow"
    @close="onClose"
  >
    <template #content>
      <div class="pb-12">
        <GrammarTextField
          ref="textFieldRef"
          v-model:value="text"
          input-type="text"
          :label="textFieldLabel"
          :placeholder="placeholder"
          :form-associated="false"
          :counter="props.maxCharacters"
          :type="inputFieldType"
          :message="displayedErrorMessage"
          @keydown="onKeyDown"
        />
      </div>
    </template>

    <template #footer>
      <GrammarFooter>
        <template #left-button>
          <GrammarSecondaryButton :label="cancelLabel" @click="onClose" />
        </template>
        <template #right-button>
          <GrammarPrimaryButton
            :label="okLabel"
            :disabled="!canSave"
            @click="onSave"
          />
        </template>
      </GrammarFooter>
    </template>
  </GrammarModal>
</template>
